import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import type { DiscoveryProduct } from "@/components/discovery/types";
import type { UseListingBrowseProductsArgs } from "./listing-browse-query";
import type { BrowseListing } from "./marketplace-listing";

type NearbyResult = FunctionReturnType<
  typeof api.products.nearbyQueries.getNearbyWithFreightPaginated
>;
type SmartFallbackResult = FunctionReturnType<
  typeof api.products.nearbyQueries.getSmartNearbyWithFreight
>;
type FallbackResult = FunctionReturnType<
  typeof api.products.marketplaceQueries.listMarketplacePage
>;

export interface ListingBrowseResultArgs {
  filters: UseListingBrowseProductsArgs;
  hasLocation: boolean;
  hasSearch: boolean;
  page: number;
  pageSize: number;
  searchResults: DiscoveryProduct[];
  searchIsSettled: boolean;
  searchTruncated?: boolean;
  nearbyResult: NearbyResult | undefined;
  fallbackResult: FallbackResult | undefined;
  shouldUseSmartFallback: boolean;
  smartFallbackResult: SmartFallbackResult | undefined;
}

export function resolveListingBrowseResult(args: ListingBrowseResultArgs) {
  if (args.hasSearch) return resolveSearchResult(args);

  const initialResult = resolveInitialResult(args);
  if (initialResult) return initialResult;

  if (args.hasLocation) return resolveLocationResult(args);
  if (args.fallbackResult?.listings) return resolveFallbackResult(args);

  const products: BrowseListing[] = [];
  return { products, totalCount: null, hasNextPage: false };
}

function resolveSearchResult(args: ListingBrowseResultArgs) {
  const start = (args.page - 1) * args.pageSize;
  return {
    products: args.searchResults.slice(start, start + args.pageSize),
    totalCount: args.searchIsSettled && !args.searchTruncated ? args.searchResults.length : null,
    truncated: args.searchTruncated ?? false,
    hasNextPage: false,
  };
}

function resolveInitialResult(args: ListingBrowseResultArgs) {
  const filters = args.filters;
  const canUseInitial =
    args.page === 1 &&
    filters.conditions.length === 0 &&
    filters.priceRange.min === null &&
    filters.priceRange.max === null &&
    filters.specFilters.length === 0 &&
    filters.stateCode === null &&
    filters.listedAfterDays === null &&
    filters.intentFilter === "all" &&
    filters.quantity <= 1 &&
    !filters.searchQuery.trim() &&
    !args.hasLocation &&
    !!filters.initialListings &&
    args.fallbackResult === undefined;
  if (!canUseInitial) return null;

  return {
    products: filters.initialListings!,
    totalCount: filters.initialTotalCount ?? filters.initialListings!.length,
    hasNextPage: false,
    fromServerRender: true,
  };
}

function resolveLocationResult(args: ListingBrowseResultArgs) {
  if (args.shouldUseSmartFallback) {
    const products = args.smartFallbackResult?.products ?? [];
    return {
      products,
      totalCount:
        args.smartFallbackResult && !args.smartFallbackResult.metadata.truncated
          ? products.length
          : null,
      truncated: args.smartFallbackResult?.metadata.truncated ?? false,
      hasNextPage: false,
      isExpandedSearch:
        args.smartFallbackResult?.metadata.searchType !== "local",
    };
  }

  return {
    products: args.nearbyResult?.products ?? [],
    totalCount: args.nearbyResult?.pagination?.total ?? null,
    truncated: args.nearbyResult?.pagination?.truncated ?? false,
    hasNextPage: args.nearbyResult?.pagination?.isDone === false,
  };
}

function resolveFallbackResult(args: ListingBrowseResultArgs) {
  return {
    products: args.fallbackResult?.listings ?? [],
    totalCount: args.fallbackResult?.pagination?.total ?? null,
    hasNextPage: args.fallbackResult?.pagination?.isDone === false,
  };
}

export function getListingBrowseLoadingState(args: {
  hasSearch: boolean;
  searchStatus: string;
  searchResultCount: number;
  requiredSearchResults: number;
  hasLocation: boolean;
  nearbyQueryActive: boolean;
  nearbyResultLoaded: boolean;
  fallbackQueryActive: boolean;
  fallbackResultLoaded: boolean;
  smartFallbackLoading: boolean;
  stateLookupPending: boolean;
}): boolean {
  const searchLoading =
    args.hasSearch &&
    (args.searchStatus === "LoadingFirstPage" ||
      (args.searchResultCount < args.requiredSearchResults &&
        args.searchStatus !== "Exhausted"));
  const standardLoading =
    !args.hasSearch &&
    ((args.hasLocation && args.nearbyQueryActive && !args.nearbyResultLoaded) ||
      (!args.hasLocation &&
        args.fallbackQueryActive &&
        !args.fallbackResultLoaded));
  return (
    args.stateLookupPending ||
    searchLoading ||
    standardLoading ||
    args.smartFallbackLoading
  );
}
